import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses
} from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import { styled, SxProps, Theme } from '@mui/material/styles';
import AssistantCitation from 'ai-assistant/citations/assistant-citation';
import React from 'react';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));
const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
  {
    transform: 'rotate(90deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    justifyContent: 'space-between',
    marginLeft: theme.spacing(1)
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  }),
}));
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, 0.125)',
}));
const Title = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightMedium,
})) as typeof Typography;
const Score = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
})) as typeof Typography;
const Excerpt = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  '& mark': {
    backgroundColor: 'rgba(255, 255, 0, 0.5)',
    padding: '0 2px',
    borderRadius: '2px'
  },
})) as typeof Typography;

type CitationsProps = {
  className?: string;
  sx?: SxProps<Theme>;
  citations: AssistantCitation[];
};

export default function Citations(props: Readonly<CitationsProps>) {
  const {
    className,
    sx,
    citations
  } = props;

  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box className={className} sx={sx}>
      <Typography variant="subtitle2" gutterBottom component="span" sx={{ ml: 1 }}>
        Citations
      </Typography>
      {citations.map((citation: AssistantCitation, index: number) => (
        <Accordion
          key={citation.fileId.value}
          expanded={expanded === `panel${index + 1}`}
          onChange={handleChange(`panel${index + 1}`)}
        >
          <AccordionSummary>
            <Title component="span">
              {`${index + 1}. `}
              {citation.fileName}
              {citation.page ? `(Page ${citation.page})` : null}
            </Title>
            <Score component="span">
              {`${(citation.combinedScore ?? 0) * 100}% match`}
            </Score>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" gutterBottom component="div">
              Relevant Excerpt:
            </Typography>
            <Excerpt
              component="div"
              dangerouslySetInnerHTML={{ __html: citation.text ?? '' }}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
